/**
 * Admin — Database Backup (super_admin only).
 *
 * Mirrors the mobile Database Backup screen:
 *   1. Request backup → OTP emailed to the requester.
 *   2. Enter code → backend returns { password, download_token }.
 *   3. Modal shows the password ONCE and offers the encrypted-ZIP download.
 *   4. Backup history section shows the audit log.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useApi, API_BASE } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, Check, KeyRound, Clock, Mail, AlertTriangle, RefreshCcw, Copy, Download } from "lucide-react";

function How({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <Icon size={18} className="text-primary shrink-0 mt-0.5" />
      <div className="text-sm">{children}</div>
    </div>
  );
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function AdminBackupPage() {
  const { user, token } = useAuth();
  const api = useApi();
  const nav = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";

  const [stage, setStage] = useState("idle"); // idle | code | reveal
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [downloadToken, setDownloadToken] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    if (!isSuperAdmin) { setLoadingHistory(false); return; }
    setLoadingHistory(true);
    try {
      const r = await api("/admin/backup/history");
      if (r.ok) { const d = await r.json(); setHistory(d.history || []); }
    } finally { setLoadingHistory(false); }
  }, [api, isSuperAdmin]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      const r = await api("/admin/backup/request", { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || "Could not send code"); return; }
      setStage("code");
      if (d.dev_otp) { setCode(d.dev_otp); toast.message(`(Preview) OTP auto-filled: ${d.dev_otp}`); }
      else { toast.success(`A 6-digit code has been emailed to ${d.email}.`); }
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    const c = code.trim();
    if (c.length !== 6) return toast.error("Enter the 6-digit code from your email.");
    setBusy(true);
    try {
      const r = await api("/admin/backup/confirm", { method: "POST", body: JSON.stringify({ code: c }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || "Could not confirm"); return; }
      setPassword(d.password);
      setDownloadToken(d.download_token);
      setDownloaded(false);
      setStage("reveal");
    } finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/admin/backup/download?token=${encodeURIComponent(downloadToken)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        let detail = "Download failed";
        try { const d = await resp.json(); detail = d?.detail || detail; } catch { /* not json */ }
        toast.error(detail);
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `middlesex-connect-backup-${Date.now()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setDownloaded(true);
      toast.success(`Downloaded ${filename}`);
      loadHistory();
    } finally { setBusy(false); }
  };

  const dismissReveal = () => {
    setStage("idle"); setCode(""); setPassword(""); setDownloadToken(""); setDownloaded(false);
  };

  const copyPassword = async () => {
    try { await navigator.clipboard.writeText(password); toast.success("Password copied to clipboard."); }
    catch { toast.error("Copy failed — please select and copy manually."); }
  };

  if (!isSuperAdmin) {
    return (
      <div>
        <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4">
          <ChevronLeft size={16} /> Back
        </button>
        <PageHeader title="Database Backup" subtitle="Super-admin — full encrypted export" testId="backup-header" />
        <Card className="p-6"><div className="font-semibold">Super admin access required.</div></Card>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4" data-testid="backup-back">
        <ChevronLeft size={16} /> Admin
      </button>
      <PageHeader
        title="Database Backup"
        subtitle="Super-admin — full encrypted export"
        testId="backup-header"
      />

      <Card className="p-5 mb-4 border-2 border-red-500/60" data-testid="backup-how">
        <h2 className="text-lg font-semibold text-red-600 mb-2">How this works</h2>
        <p className="text-sm">
          Downloads a single <strong>AES-256 encrypted ZIP</strong> containing every MongoDB collection
          (users, events, bookings, templates, etc.) as JSON files.
        </p>
        <div className="mt-3 space-y-1">
          <How icon={Check}>A 6-digit code is emailed to <strong>{user.email}</strong> to confirm it&apos;s you.</How>
          <How icon={KeyRound}>A random 20-character password is generated and shown <strong>once</strong> — save it before dismissing.</How>
          <How icon={Clock}>Rate limited: at most one successful backup per super-admin per hour.</How>
          <How icon={Mail}>Every other super-admin gets an FYI email when a backup is pulled.</How>
        </div>

        <Card className="p-3 mt-4 border-amber-500/40 bg-amber-50/40">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-900">
              <div className="font-medium">Handle downloaded ZIPs with care</div>
              <div>All super-admins are responsible for deleting downloaded backup ZIPs from their devices once they&apos;re done. The ZIP contains the entire database — keep it off shared machines and cloud drives.</div>
            </div>
          </div>
        </Card>

        {stage === "idle" && (
          <Button onClick={requestOtp} disabled={busy} size="lg" className="w-full mt-4" data-testid="backup-request">
            {busy ? "Sending code…" : "Request backup"}
          </Button>
        )}

        {stage === "code" && (
          <div className="mt-4 space-y-3">
            <div className="text-sm text-muted-foreground">
              We emailed a 6-digit code to <strong>{user.email}</strong>. Enter it below to authorise the backup.
            </div>
            <div className="space-y-1">
              <Label>6-digit code</Label>
              <Input
                inputMode="numeric" maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-xl tracking-[8px]"
                data-testid="backup-code"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={confirm} disabled={busy} className="flex-1" data-testid="backup-confirm">
                {busy ? "Verifying…" : "Confirm & generate"}
              </Button>
              <Button variant="ghost" onClick={() => { setStage("idle"); setCode(""); }} data-testid="backup-code-cancel">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {stage === "reveal" && (
          <div className="mt-4 space-y-3" data-testid="backup-reveal">
            <div className="p-4 rounded-md border-2 border-primary bg-primary/5">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">ZIP password — shown once</div>
              <div className="font-mono text-xl mt-2 break-all" data-testid="backup-password">{password}</div>
              <div className="text-xs text-muted-foreground mt-2">
                Save it in your password manager <em>before</em> dismissing — we cannot show it again.
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={copyPassword} data-testid="backup-copy-password">
                  <Copy size={14} className="mr-2" />Copy password
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={download} disabled={busy || downloaded} data-testid="backup-download">
                <Download size={14} className="mr-2" />
                {busy ? "Downloading…" : downloaded ? "Downloaded" : "Download encrypted ZIP"}
              </Button>
              <Button variant="ghost" onClick={dismissReveal} data-testid="backup-dismiss">
                {downloaded ? "Done" : "Dismiss"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 border-2 border-red-500/60" data-testid="backup-history-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-red-600">Backup history</h2>
          <Button variant="outline" size="sm" onClick={loadHistory} disabled={loadingHistory} data-testid="backup-history-refresh">
            <RefreshCcw size={12} className={`mr-1 ${loadingHistory ? "animate-spin" : ""}`} />Refresh
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Audit log of previous downloads. The ZIP files themselves are never stored on the server — this is just a record of who pulled a copy.
        </p>
        {loadingHistory ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : history.length === 0 ? (
          <Card className="p-3 border-red-500/30 bg-red-50/40">
            <div className="font-semibold text-sm">Not Found</div>
            <div className="text-sm text-center text-muted-foreground mt-1">No backups have been downloaded yet.</div>
          </Card>
        ) : (
          <div className="divide-y divide-border">
            {history.map((h, i) => (
              <div key={i} className="py-2 flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{h.name || h.email}</div>
                  <div className="text-xs text-muted-foreground">{h.email}</div>
                  <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("en-GB")}</div>
                </div>
                <div className="text-right text-xs text-muted-foreground shrink-0">
                  <div className="font-mono">{h.filename}</div>
                  <div>{fmtBytes(h.size_bytes)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
